/**
 * AppViewModel.ts
 *
 * ViewModel for managing application initialization, user authentication, and route permissions.
 */
import Cookies from "js-cookie";
import * as Sentry from "@sentry/react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "../store/slice/userSlice";
import { decryptInitData, decryptStringFromBytes } from "../utils/decrypt";
import { RootState } from "../store/types";
import logger from "../utils/logger";
import { ROUTES } from "../route/RouteConstant";
import { clearCookiesAndRedirect } from "../utils";
import { USER_DETAILS_STRINGS } from "../constant/stringConstant";

const ENCRYPTION_KEY = import.meta.env.VITE_ENCRYPTION_KEY


/**
 * AppViewModel
 * Handles app initialization, user authentication, and permission logic.
 */
export const AppViewModel = () => {
  const [pathName, setPathName] = useState<string | null>(null);
  const [allowed, setAllowed] = useState(false);
  const [appReady, setAppReady] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [userDetailsError, setUserDetailsError] = useState<string>("");
  const dispatch = useDispatch();
  const hasFetchedPlantsRef = useRef<boolean>(false);
  const userData = useSelector<RootState, Record<string, any>>(
    (state) => state.user.userData as Record<string, any>
  );

  useEffect(() => {
    if (window.location.search.includes("logout=true")) {
      clearCookiesAndRedirect();
    }
  }, []);

  useEffect(() => {
    try {

      const path = window.location.pathname;
      if (path === ROUTES.CUSTOMER_360) {
        setPathName(path);
        return
      }
      setAllowed(true);
      const urlParams = new URLSearchParams(window.location.search);
      const encryptedInitData = urlParams.get("initData");
      if (encryptedInitData) {
        const parsedCookies = decryptInitData(encryptedInitData);
        if (parsedCookies && Object.keys(parsedCookies).length > 0) {
          Object.entries(parsedCookies).forEach(([key, value]) => {
            Cookies.set(key, value as string, {
              secure: true,
              sameSite: "None",
              path: "/",
            });
          });

          dispatch(
            setUser({
              name: parsedCookies.name || "",
              email: parsedCookies.email || "",
              userData: parsedCookies,
            })
          );

          setAppReady(true);
          const cleanUrl = window.location.origin + window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
        }
      } else {
        const existingCookies = Cookies.get();
        if (existingCookies && Object.keys(existingCookies).length > 0) {
          dispatch(
            setUser({
              name: existingCookies.name || "",
              email: existingCookies.email || "",
              userData: existingCookies,
            })
          );
          setAppReady(true);
        }
      }
    } catch (error) {
      logger.error(`${AppViewModel.name} - useEffect (initData handling)`, error);
    }
  }, [dispatch]);

  useEffect(() => {
   if(!hasFetchedPlantsRef.current){
     const fetchUserDetails = async () => {
       setUserDetailsLoading(true);
     setUserDetailsError(USER_DETAILS_STRINGS.FETCHING);
      try {
        const key = ENCRYPTION_KEY;
        const encryptedEmail = userData?.["husk.user"];

        if (encryptedEmail) {
          const decryptedEmail = decryptStringFromBytes(encryptedEmail, key, key);
          if (decryptedEmail) {
            dispatch(
              setUser({
                name: userData?.name || "",
                email: decryptedEmail,
                userData,
                userDetails:  {},
              })
            );
            setUserId(null);
            // Example: Set user session info after login or when user info is available
            Sentry.setUser({
              id: decryptedEmail,
              email: decryptedEmail,
            });
            setUserDetailsLoading(false);
            setUserDetailsError("");
          }
        }
      } catch (error:any) {
        logger.error(`${AppViewModel.name} - fetchUserDetails`, error);
        if (error && typeof error === "object" && (error as any).response && ((error as any).status === 500 || (error as any).status === 404)) {
          setUserDetailsLoading(true);
          setUserDetailsError(USER_DETAILS_STRINGS.ERROR);
        }
        else {
          setUserDetailsError("");
          setUserDetailsLoading(false);
        }
      }
    };

    try {
      if (allowed && appReady && userData && Object.keys(userData).length > 0) {
        fetchUserDetails();
        hasFetchedPlantsRef.current = true;
      }
    } catch (error) {
      logger.error(`${AppViewModel.name} - useEffect (fetchUserDetails trigger)`, error);
    }
   }
  }, [allowed, appReady, userData, dispatch]);

  return {
    allowed,
    appReady,
    userData,
    userId,
    pathName,
    userDetailsLoading,
    userDetailsError
  };
};